import { inputTypes } from '../restrictions.model'
import CustomSwitch from '../inputs/custom-switch'
import { useUpdateCustomer } from 'services/queries/use-update-customer'
import { useParams } from 'react-router'
import CustomSelect from '../inputs/single-select'
import MultiSelect from '../inputs/multi-select'
import ScoreModels from '../inputs/score-models'
import BeneficialOwners from '../inputs/beneficial-owners'
import GlobalData from '../inputs/global-data'
import RisikaAi from '../inputs/risika-ai'
import { DateSubConfig, Element, NewValue } from '../restrictions.types'
import { handleUpdateRestrictions } from '../restrictions.controller'
import { BeneficialOwnersType } from 'services/cm-api/get-customer'

type CreateInputElementProps = {
  data: any
  element: Element
  // De-emphasised but still editable (the "Included in every package" group).
  muted?: boolean
}
const CreateInputElement = ({
  data,
  element,
  muted = false,
}: CreateInputElementProps) => {
  const { type, label } = element
  const { mutate: updateCustomer, isLoading } = useUpdateCustomer()
  const { id: selectedCustomer } = useParams()
  const handleUpdateCustomer = (
    type: 'boolean' | 'select' | 'numeric' | 'scoreModel',
    newValue?: NewValue,
  ) => {
    switch (type) {
      case 'boolean':
        handleUpdateRestrictions({
          selectedCustomer,
          updateCustomer,
          newValue: !getElementValue(),
          element,
        })
        break
      case 'select':
      case 'numeric':
      case 'scoreModel':
        handleUpdateRestrictions({
          selectedCustomer,
          updateCustomer,
          newValue,
          element,
        })
        break

      default:
        break
    }
  }

  const getElementValue = () => {
    if (element.updateLocation) {
      return data[element.updateLocation][element.key]
    }
    return data[element.key]
  }

  // ENG2-2395 — the Global Data composite carries two date sub-configs
  // (startDate / endDate) that live alongside the toggle in rights.global.
  // ENG2-2614 — Risika for AI carries one (endDate) the same way.
  const readGlobalDataDate = (sub?: DateSubConfig): string | null =>
    sub && sub.updateLocation
      ? (data[sub.updateLocation]?.[sub.key] as string | null) ?? null
      : null

  const updateGlobalDataDate = (
    sub: DateSubConfig | undefined,
    isoDate: string | null,
  ) =>
    handleUpdateRestrictions({
      selectedCustomer,
      updateCustomer,
      newValue: isoDate,
      element: { key: sub?.key ?? '', updateLocation: sub?.updateLocation },
    })

  switch (type) {
    case inputTypes.BOOLEAN:
      return (
        <CustomSwitch
          isLoading={isLoading}
          onChange={() => handleUpdateCustomer('boolean')}
          value={getElementValue()}
          label={label}
          muted={muted}
        />
      )
    case inputTypes.SINGLE_SELECT:
      return (
        <CustomSelect
          isLoading={isLoading}
          onChange={(selectedValue) =>
            handleUpdateCustomer('select', selectedValue)
          }
          value={data[element.key]}
          label={label}
          options={element.options}
        />
      )
    case inputTypes.MULTI_SELECT:
      return (
        <MultiSelect
          isLoading={isLoading}
          onChange={(selectedValue) =>
            handleUpdateCustomer('select', selectedValue)
          }
          value={data[element.key]}
          label={label}
          options={element.options}
        />
      )
    case inputTypes.SCORE_MODEL:
      return (
        <ScoreModels
          isLoading={isLoading}
          onChange={(selectedValue) =>
            handleUpdateCustomer('scoreModel', selectedValue)
          }
          value={data[element.key]}
          label={label}
          options={element.options}
          availableModels={element.availableModels}
        />
      )
    case inputTypes.BENEFICIAL_OWNERS:
      return (
        <BeneficialOwners
          isLoading={isLoading}
          onChange={(newValue: BeneficialOwnersType) =>
            handleUpdateRestrictions({
              selectedCustomer,
              updateCustomer,
              newValue,
              element,
            })
          }
          value={getElementValue() as BeneficialOwnersType}
          label={label}
          reasonOptions={element.reasonOptions || []}
        />
      )
    case inputTypes.RISIKA_AI:
      return (
        <RisikaAi
          isLoading={isLoading}
          enabled={getElementValue() as boolean}
          endDateValue={readGlobalDataDate(element.endDate)}
          label={label}
          endDateLabel={element.endDate?.label ?? 'Risika for AI ends'}
          muted={muted}
          onToggle={() => handleUpdateCustomer('boolean')}
          onEndDateChange={(isoDate: string | null) =>
            updateGlobalDataDate(element.endDate, isoDate)
          }
        />
      )
    case inputTypes.GLOBAL_DATA:
      return (
        <GlobalData
          isLoading={isLoading}
          enabled={getElementValue() as boolean}
          startDateValue={readGlobalDataDate(element.startDate)}
          endDateValue={readGlobalDataDate(element.endDate)}
          label={label}
          startDateLabel={element.startDate?.label ?? 'Subscription start date'}
          endDateLabel={element.endDate?.label ?? 'Subscription end date'}
          onToggle={() =>
            handleUpdateRestrictions({
              selectedCustomer,
              updateCustomer,
              newValue: !getElementValue(),
              element,
            })
          }
          onStartDateChange={(isoDate: string | null) =>
            updateGlobalDataDate(element.startDate, isoDate)
          }
          onEndDateChange={(isoDate: string | null) =>
            updateGlobalDataDate(element.endDate, isoDate)
          }
        />
      )
    default:
      return null
  }
}

export default CreateInputElement
